import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  CheckRoundOutlineIcon,
  Heading,
  NavigationToggler,
  Option,
  Text,
  useResponsive,
} from '@common-fe/common-fe';

import useReceiptsFeatureFlag from '@/modules/MyDocuments/hooks/useReceiptsFeatureFlag';
import useEmployeeStore from '@/modules/user/stores/useEmployee.store';

import { MyDocumentsTabs, MyDocumentsTabsTitle } from '../myDocuments.types';

interface Props {
  tab: string;
  setTab: (tab: string) => void;
}

const MyDocumentsHeader: React.FC<Props> = ({ tab, setTab }) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const { isReceiptsEnabled } = useReceiptsFeatureFlag();
  const { employee } = useEmployeeStore();

  const tabOptions = useMemo<Option[]>(
    () => [
      ...(isReceiptsEnabled
        ? [
            {
              key: MyDocumentsTabs.RECEIPTS,
              value: MyDocumentsTabs.RECEIPTS,
              title: t('Saved Receipts'),
            },
          ]
        : []),
      {
        key: MyDocumentsTabs.LMN,
        value: MyDocumentsTabs.LMN,
        title: t('Letters of Medical Necessity'),
      },
      {
        key: MyDocumentsTabs.STATEMENTS,
        value: MyDocumentsTabs.STATEMENTS,
        title: t('Statements'),
      },
      {
        key: MyDocumentsTabs.TAX_FORMS,
        value: MyDocumentsTabs.TAX_FORMS,
        title: t('Tax Forms'),
      },
    ],
    [t, isReceiptsEnabled]
  );

  const showStatementsMessage = useMemo(
    () => employee?.isElectronicStatementOnly && tab === MyDocumentsTabs.STATEMENTS,
    [employee?.isElectronicStatementOnly, tab]
  );

  return (
    <Box
      direction={isMobile ? 'column' : 'row'}
      justify="between"
      pad={isMobile ? { horizontal: 'spacing16' } : {}}
      align={isMobile ? 'start' : 'center'}
      gap="spacing16"
    >
      <Box direction={isMobile ? 'column' : 'row'} align="center" gap="spacing12" flex wrap fill={isMobile}>
        <Heading
          data-testId="my-documents-page_page-title"
          textAlign={isMobile ? 'center' : undefined}
          level={isMobile ? 2 : 1}
          color="textTitle"
          size="regular"
        >
          {MyDocumentsTabsTitle?.[tab as MyDocumentsTabs]}
        </Heading>

        {showStatementsMessage ? (
          <Box data-testId="paperless-statement-message" direction="row" align="center" gap="spacing4">
            <CheckRoundOutlineIcon color="success" size="24px" />
            <Text color="success" size="large">
              You've opted-in to paperless statements
            </Text>
          </Box>
        ) : null}
      </Box>
      <Box fill={isMobile}>
        <NavigationToggler
          testId="my-documents-page"
          value={tab}
          {...(isMobile
            ? {
                width: {
                  min: 'auto',
                },
                pad: {
                  horizontal: '24px',
                  vertical: '10px',
                },
                height: '60px',
              }
            : {})}
          onChange={setTab}
          options={tabOptions}
          textStyle={
            isMobile
              ? {
                  display: '-webkit-box',
                  fontSize: '14px',
                  lineHeight: '20px',
                  maxHeight: '40px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'normal',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 2,
                }
              : undefined
          }
          containerStyle={
            isMobile
              ? {
                  width: '100%',
                }
              : {}
          }
          titleRenderWrapper={(titleNode, key) =>
            isMobile && key === MyDocumentsTabs.LMN ? <Box width="67px">{titleNode}</Box> : titleNode
          }
        />
      </Box>
    </Box>
  );
};

export default MyDocumentsHeader;
